import clsx from "clsx"
import { useState } from "react"

import useCurrencyFormatters from "@hooks/useCurrencyFormatters"

import { faIcon } from "@utils/faIcon"

import { Badge, Icon } from "@atoms/index"

import ListItem from "@molecules/ListItem/ListItem"
import ListItemContent from "@molecules/ListItem/ListItemContent/ListItemContent"

import { formatModifierAdjustment } from "@pages/Settings/SettingsCatalogs/SettingsCatalogsItemDetails/SettingsCatalogsItemDetails.utils"

import styles from "./ModifierGroupRow.module.scss"
import { ModifierGroupRowProps } from "./ModifierGroupRow.types"

// Typed-entry groups have no options to list; the caption names the kind of value the store asks for instead.
const ENTRY_KIND_LABELS: Record<string, string> = {
    text: "Text entry",
    multi_line_text: "Text entry",
    numbers_only_text: "Number entry",
    date: "Date entry",
    file: "File upload",
}

export default function ModifierGroupRow(props: ModifierGroupRowProps) {
    const { group } = props

    const { formatCurrency } = useCurrencyFormatters()

    const [isExpanded, setIsExpanded] = useState(false)

    // A checkbox group's two options are just its checked and unchecked states, so the row shows the
    // storefront's checkbox text (with the checked state's price effect) instead of an option list.
    const isCheckbox = group.modifier_type === "checkbox"
    const checkedOption = isCheckbox ? group.modifiers.find((modifier) => modifier.is_checked_state) : undefined

    const modifierCount = group.modifiers.length
    const hasOptions = modifierCount > 0 && !isCheckbox

    const adjustment = checkedOption ? formatModifierAdjustment(checkedOption, formatCurrency) : ""
    const checkboxCaption = isCheckbox && group.checkbox_label ? `${group.checkbox_label}${adjustment}` : ""

    let caption
    if (isCheckbox) {
        caption = "Checkbox"
    } else if (hasOptions) {
        caption = `${modifierCount} ${modifierCount === 1 ? "option" : "options"}`
    } else {
        caption = ENTRY_KIND_LABELS[group.modifier_type] ?? "Free entry"

        if (group.text_max_length !== null) {
            caption = `${caption} (max ${group.text_max_length} characters)`
        }
    }
    // ListItem's props are a discriminated union, so the clickable pair travels together.
    const rootProps = hasOptions
        ? ({ isClickable: true, onClick: () => setIsExpanded((previousIsExpanded) => !previousIsExpanded) } as const)
        : ({ isClickable: false } as const)

    return (
        <div>
            <ListItem {...rootProps} aria-expanded={hasOptions ? isExpanded : undefined}>
                <ListItem.AvatarAndContent
                    content={
                        <ListItemContent
                            leftLabel={
                                <ListItem.Label
                                    label={group.name}
                                    suffixSlot={group.is_required ? <Badge size="sm">Required</Badge> : undefined}
                                />
                            }
                            leftCaption={
                                checkboxCaption ? (
                                    <ListItem.Caption caption={checkboxCaption} truncate={1} />
                                ) : undefined
                            }
                            rightCaption={<ListItem.Caption caption={caption} truncate={1} />}
                        />
                    }
                />
                {hasOptions && (
                    <ListItem.Actions>
                        <Icon
                            size={14}
                            icon={faIcon({ name: "angle-right", style: "regular" })}
                            container="fixedHeight"
                            className={clsx(styles.chevron, { [styles.chevronExpanded]: isExpanded })}
                        />
                    </ListItem.Actions>
                )}
            </ListItem>
            {isExpanded && (
                <ul className={styles.modifierList}>
                    {group.modifiers.map((modifier) => (
                        <li key={modifier.external_id} title={modifier.purchasing_disabled_message || undefined}>
                            <Badge size="sm">
                                {modifier.name}
                                {formatModifierAdjustment(modifier, formatCurrency)}
                                {!modifier.is_purchasable && " (unavailable)"}
                            </Badge>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
